<template>
  <v-col class="help-coverage-content">
    <v-row>
      <v-col>
        <docs-hero-banner
          eyebrow="Internal QA · Phase 2"
          title="Help Coverage Audit"
          description="Route-level snapshot of guided tours, page guides, and help-menu action inventory. Use this to prioritize new tour authoring and label tuning."
          :meta-items="heroMetaItems"
        />
      </v-col>
    </v-row>

    <!-- Summary stat cards -->
    <v-row class="mb-2">
      <v-col
        v-for="stat in summaryStats"
        :key="stat.label"
        cols="12"
        sm="4"
      >
        <v-card
          outlined
          class="pa-4 text-center coverage-stat-card"
        >
          <div :class="`display-1 font-weight-bold ${stat.colorClass}`">
            {{ stat.value }}
          </div>

          <div class="subtitle-2 font-weight-medium mt-1">
            {{ stat.label }}
          </div>

          <div class="caption grey--text mt-1">
            {{ stat.note }}
          </div>
        </v-card>
      </v-col>
    </v-row>

    <!-- Tour gap callout -->
    <v-row>
      <v-col cols="12">
        <docs-callout
          title="Tour gaps — these routes have no guided tour yet"
          color="warning"
          :icon="['fas', 'triangle-exclamation']"
        >
          <span
            v-for="(route, i) in routesNeedingTours"
            :key="route.key"
          >
            <strong>{{ route.label }}</strong>
            <span v-if="i < routesNeedingTours.length - 1">,&nbsp;</span>
          </span>
          <span class="ml-1 grey--text">({{ routesNeedingTours.length }} routes)</span>
        </docs-callout>
      </v-col>
    </v-row>

    <!-- Route detail table -->
    <v-row>
      <v-col cols="12">
        <docs-section-card
          title="Route Coverage Detail"
          description="Current tour and page-guide wiring per route. Tour ID links to tourDefinitions.js."
          :icon="['fas', 'table-list']"
          eyebrow="Coverage"
        >
          <v-data-table
            :headers="routeTableHeaders"
            :items="routes"
            dense
            disable-sort
            hide-default-footer
            class="elevation-0 coverage-table"
          >
            <template v-slot:item.hasTour="{ item }">
              <v-chip
                x-small
                :color="item.hasTour ? 'success' : 'warning'"
                text-color="white"
                class="text-none font-weight-medium"
              >
                <font-awesome-icon
                  :icon="item.hasTour ? ['fas', 'circle-check'] : ['fas', 'circle-xmark']"
                  class="mr-1"
                  size="xs"
                />
                {{ item.hasTour ? 'Yes' : 'No' }}
              </v-chip>
            </template>

            <template v-slot:item.tourId="{ item }">
              <code
                v-if="item.tourId"
                class="caption"
              >
                {{ item.tourId }}
              </code>

              <span
                v-else
                class="caption grey--text"
              >
                —
              </span>
            </template>

            <template v-slot:item.guideAnchor="{ item }">
              <a
                :href="item.guideAnchor"
                target="_blank"
                rel="noopener"
                class="caption text-decoration-none"
              >
                {{ item.guideAnchor }}
              </a>
            </template>
          </v-data-table>
        </docs-section-card>
      </v-col>
    </v-row>

    <!-- Help menu action inventory -->
    <v-row>
      <v-col cols="12">
        <docs-section-card
          title="Help Menu Action Inventory"
          description="All actions exposed in the header Help menu, grouped by section and audience."
          :icon="['fas', 'circle-question']"
          eyebrow="Inventory"
        >
          <div
            v-for="section in helpMenuInventory"
            :key="section.id"
            class="mb-4"
          >
            <div class="caption text-uppercase font-weight-bold grey--text mb-2">
              {{ section.title }} · {{ section.audience }}
            </div>

            <v-data-table
              :headers="actionTableHeaders"
              :items="section.items"
              dense
              disable-sort
              hide-default-footer
              class="elevation-0 coverage-table mb-2"
            >
              <template v-slot:item.type="{ item }">
                <v-chip
                  x-small
                  outlined
                  class="text-none"
                >
                  {{ item.type }}
                </v-chip>
              </template>

              <template v-slot:item.routeAware="{ item }">
                <font-awesome-icon
                  :icon="item.routeAware ? ['fas', 'circle-check'] : ['fas', 'minus']"
                  :class="item.routeAware ? 'success--text' : 'grey--text'"
                  size="sm"
                />
              </template>
            </v-data-table>
          </div>
        </docs-section-card>
      </v-col>
    </v-row>

    <!-- Next steps callout -->
    <v-row>
      <v-col cols="12">
        <docs-callout
          title="Recommended next steps"
          color="info"
          :icon="['fas', 'list-check']"
        >
          <ol class="pl-3 mb-0">
            <li class="mb-1">
              Author tours for Organization, Person, Pacing, and Discrepancy to reach 100% route coverage.
            </li>
            <li class="mb-1">
              Use <code>help_menu_action</code> Firebase Analytics events to identify which actions users click most / least.
            </li>
            <li class="mb-1">
              Re-sort help menu items once 30 days of action telemetry is available.
            </li>
            <li>
              Add route-specific tour step hints (role-based step skipping) for power users.
            </li>
          </ol>
        </docs-callout>
      </v-col>
    </v-row>
  </v-col>
</template>

<script>
export default {
  name: 'HelpCoverageContent',
  components: {
    DocsCallout: () => import('@/docs/components/DocsCallout.vue'),
    DocsHeroBanner: () => import('@/docs/components/DocsHeroBanner.vue'),
    DocsSectionCard: () => import('@/docs/components/DocsSectionCard.vue'),
  },
  computed: {
    heroMetaItems() {
      return [
        { label: 'Internal Dev Only', icon: ['fas', 'lock'] },
        { label: 'Last reviewed April 2026', icon: ['fas', 'calendar'] },
        { label: 'Phase 2 Help Redesign', icon: ['fas', 'compass'] },
      ]
    },

    routes() {
      return [
        {
          key: 'time',
          label: 'Time',
          hasTour: true,
          tourId: 'time-entry-quick-start',
          guideAnchor: '/docs/guides#time',
        },
        {
          key: 'projects',
          label: 'Projects',
          hasTour: true,
          tourId: 'projects-dashboard-overview',
          guideAnchor: '/docs/guides#projects',
        },
        {
          key: 'admin',
          label: 'Admin Projects',
          hasTour: true,
          tourId: 'admin-projects-intro',
          guideAnchor: '/docs/guides#admin',
        },
        {
          key: 'organization',
          label: 'Organization',
          hasTour: false,
          tourId: null,
          guideAnchor: '/docs/guides#organization',
        },
        {
          key: 'person',
          label: 'Person',
          hasTour: false,
          tourId: null,
          guideAnchor: '/docs/guides#person',
        },
        {
          key: 'pacing',
          label: 'Pacing',
          hasTour: false,
          tourId: null,
          guideAnchor: '/docs/guides#pacing',
        },
        {
          key: 'discrepancy',
          label: 'Discrepancy',
          hasTour: false,
          tourId: null,
          guideAnchor: '/docs/guides#discrepancy',
        },
      ]
    },

    routesWithTours() {
      return this.routes.filter((r) => r.hasTour)
    },

    routesNeedingTours() {
      return this.routes.filter((r) => !r.hasTour)
    },

    tourCoveragePercent() {
      return Math.round((this.routesWithTours.length / this.routes.length) * 100)
    },

    guideCoveragePercent() {
      return 100
    },

    summaryStats() {
      return [
        {
          label: 'Tour Coverage',
          value: `${this.tourCoveragePercent}%`,
          note: `${this.routesWithTours.length} of ${this.routes.length} routes`,
          colorClass: this.tourCoveragePercent >= 80 ? 'success--text' : 'warning--text',
        },
        {
          label: 'Page Guide Coverage',
          value: `${this.guideCoveragePercent}%`,
          note: `${this.routes.length} of ${this.routes.length} routes`,
          colorClass: 'success--text',
        },
        {
          label: 'Tours Needed',
          value: this.routesNeedingTours.length,
          note: 'Opportunity areas',
          colorClass: this.routesNeedingTours.length === 0 ? 'success--text' : 'warning--text',
        },
      ]
    },

    routeTableHeaders() {
      return [
        { text: 'Route', value: 'label', width: '16%' },
        { text: 'Tour', value: 'hasTour', width: '10%' },
        { text: 'Tour ID', value: 'tourId', width: '32%' },
        { text: 'Page Guide Anchor', value: 'guideAnchor' },
      ]
    },

    actionTableHeaders() {
      return [
        { text: 'Action', value: 'title', width: '32%' },
        { text: 'Type', value: 'type', width: '16%' },
        { text: 'Route-aware', value: 'routeAware', width: '14%' },
        { text: 'Notes', value: 'notes' },
      ]
    },

    helpMenuInventory() {
      return [
        {
          id: 'internal-primary',
          title: 'Page Actions',
          audience: 'Internal users',
          items: [
            {
              title: 'Resume Tour',
              type: 'emit',
              routeAware: true,
              notes: 'Only enabled when saved progress exists for the current route',
            },
            {
              title: 'Start Guided Tour',
              type: 'emit',
              routeAware: true,
              notes: 'Disabled on routes with no tour definition',
            },
            {
              title: 'Open Page Guide',
              type: 'navigate',
              routeAware: true,
              notes: 'Subtitle adapts by route; resolves to /docs/guides#<route>',
            },
          ],
        },
        {
          id: 'internal-support',
          title: 'Reference and Support',
          audience: 'Internal users',
          items: [
            {
              title: 'Browse Help Docs',
              type: 'navigate',
              routeAware: false,
              notes: 'Always links to /docs/getting-started',
            },
            {
              title: 'See What Changed',
              type: 'navigate',
              routeAware: false,
              notes: 'Always links to /docs/release-summary',
            },
            {
              title: 'Keyboard Shortcuts',
              type: 'emit',
              routeAware: false,
              notes: 'Opens keyboard shortcuts modal in Layout.vue',
            },
            {
              title: 'Send Feedback',
              type: 'dialog',
              routeAware: false,
              notes: 'Opens in-app feedback dialog; context includes route + reportKey',
            },
          ],
        },
        {
          id: 'external-primary',
          title: 'Help for This Page',
          audience: 'External users',
          items: [
            {
              title: 'Open Page Guide',
              type: 'navigate',
              routeAware: true,
              notes: 'Subtitle adapts by route; resolves to /docs/guides#<route>',
            },
            {
              title: 'Browse Help Docs',
              type: 'navigate',
              routeAware: false,
              notes: 'Links to /docs/getting-started',
            },
          ],
        },
        {
          id: 'external-support',
          title: 'More Help',
          audience: 'External users',
          items: [
            {
              title: 'See What Changed',
              type: 'navigate',
              routeAware: false,
              notes: 'Links to /docs/release-summary',
            },
            {
              title: 'Keyboard Shortcuts',
              type: 'emit',
              routeAware: false,
              notes: 'Opens keyboard shortcuts modal',
            },
            {
              title: 'Send Feedback',
              type: 'dialog',
              routeAware: false,
              notes: 'Opens in-app feedback dialog',
            },
          ],
        },
      ]
    },
  },
}
</script>

<style scoped lang="scss">
.coverage-stat-card {
  border-radius: 10px;
}

.coverage-table {
  border-radius: 6px;
  border: 1px solid rgba(0, 0, 0, 0.08);
}
</style>
